import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from 'components/ui/popover';
import { Icon } from 'components/ui/icon';
import { cn } from 'lib/utils';

// Lets the pointer cross the gap into the panel to reach the "x" (WCAG 1.4.13).
const HOVER_CLOSE_DELAY_MS = 150;

const PANEL_Z_INDEX = 'z-30';
const NAVBAR_HEIGHT_PX = 64;
const COLLISION_PADDING = {
  top: NAVBAR_HEIGHT_PX + 8,
  right: 16,
  bottom: 16,
  left: 16,
};

interface IProps {
  label: string;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  'data-testid'?: string;
}

/**
 * Info icon with dismissible helper copy; `readme.md` covers when to use this
 * over `ui/tooltip`. Radix's Popover trigger *toggles* on click, which would
 * close an already-hover-open panel, so we `preventDefault()` our own onClick —
 * making Radix skip `onOpenToggle` — and own `open` outright.
 */
const InfoPopover = ({
  label,
  children,
  side = 'top',
  className,
  'data-testid': dataTestId = 'info-popover',
}: IProps): JSX.Element => {
  const [open, setOpen] = useState(false);

  // "Pinned" = opened by click/tap/Enter rather than hover, and so immune to
  // pointer-leave. A ref, not state: `onOpenAutoFocus` reads it from inside
  // FocusScope's layout effect.
  const pinned = useRef(false);
  // Swallows the synthetic mouseenter fired at the trigger when the panel
  // unmounts under the cursor, which would otherwise instantly reopen it.
  const suppressHover = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  const close = useCallback(() => {
    clearCloseTimer();
    pinned.current = false;
    suppressHover.current = true;
    setOpen(false);
  }, [clearCloseTimer]);

  const scheduleHoverClose = useCallback(() => {
    suppressHover.current = false;
    if (pinned.current) return;
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  const handleHoverOpen = useCallback(() => {
    if (suppressHover.current) {
      suppressHover.current = false;
      return;
    }
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const handleTriggerClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      if (open && pinned.current) {
        close();
        return;
      }

      clearCloseTimer();
      const wasHoverOpen = open;
      pinned.current = true;
      setOpen(true);
      // Already mounted from hover, so onOpenAutoFocus won't fire again.
      if (wasHoverOpen) contentRef.current?.focus();
    },
    [open, close, clearCloseTimer]
  );

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        setOpen(true);
        return;
      }
      close();
    },
    [close]
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        aria-label={label}
        data-testid={dataTestId}
        onClick={handleTriggerClick}
        onMouseEnter={handleHoverOpen}
        onMouseLeave={scheduleHoverClose}
        className="inline-flex shrink-0 items-center justify-center rounded-sm align-middle text-gray-blue-60 transition-colors hover:text-gray-blue-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Icon name="info" size={16} aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        side={side}
        align="start"
        sideOffset={8}
        collisionPadding={COLLISION_PADDING}
        data-testid={`${dataTestId}-content`}
        onOpenAutoFocus={(event) => {
          if (!pinned.current) event.preventDefault();
        }}
        onMouseEnter={clearCloseTimer}
        onMouseLeave={scheduleHoverClose}
        className={cn(
          'relative w-72 space-y-2 p-3 pr-8 text-sm leading-5 text-gray-blue-80 break-words',
          // Shrink rather than spill off screen — see components/ui/tooltip.tsx.
          'max-w-[min(18rem,var(--radix-popover-content-available-width,100vw))]',
          PANEL_Z_INDEX,
          className
        )}
      >
        {children}
        <PopoverClose
          aria-label="Close"
          data-testid={`${dataTestId}-close`}
          className="absolute right-2 top-2 rounded-sm text-gray-blue-60 transition-colors hover:text-gray-blue-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Icon name="x" size={14} aria-hidden="true" />
        </PopoverClose>
      </PopoverContent>
    </Popover>
  );
};

export default InfoPopover;
